/**
 * Interactive Chat Loop - Ink Version
 * Modern React-based CLI interface using Ink
 */


import { render } from 'ink';
import os from 'os';
import { App } from '../ui/App.js';
import { agentService } from './service.js';
import { renderMarkdown } from './renderer.js';
import type { McpState, McpToolResult, AgentStreamCallbacks } from './types.js';
import type { WechatGatewayService } from '../wechat/service.js';

/**
 * Chat options
 */
export interface ChatOptions {
    mcpState?: McpState;
    onMcpToolCall?: (
        callId: string,
        serverName: string,
        toolName: string,
        args: Record<string, unknown>
    ) => Promise<McpToolResult>;
    mcpClient?: unknown;
    debug?: boolean;
    initialMessage?: string;
    resumeConversationId?: string;
    cliMode?: { enabled?: boolean };
    gatewayEnabled?: boolean;
    gatewayPollInterval?: number;
    gatewaySessionName?: string;
    wechatService?: WechatGatewayService;
    modelTier?: 'lite' | 'flash' | 'pro';
}

/**
 * Start the interactive chat loop using Ink
 */
export async function startChat(options: ChatOptions = {}): Promise<void> {
    const {
        mcpState, onMcpToolCall, mcpClient, debug,
        initialMessage, resumeConversationId,
        gatewayEnabled, gatewayPollInterval, gatewaySessionName,
        wechatService, modelTier,
    } = options;

    // Render the Ink application
    const { waitUntilExit } = render(
        <App
            mcpState={mcpState}
            onMcpToolCall={onMcpToolCall}
            mcpClient={mcpClient}
            debug={debug}
            initialMessage={initialMessage}
            resumeConversationId={resumeConversationId}
            gatewayEnabled={gatewayEnabled}
            gatewayPollInterval={gatewayPollInterval}
            gatewaySessionName={gatewaySessionName}
            wechatService={wechatService}
            modelTier={modelTier}
        />
    );

    // Wait until the app exits
    await waitUntilExit();
}

/**
 * Send a single message (non-interactive mode)
 * Used for programmatic usage and one-shot commands
 */
export async function sendMessage(
    message: string,
    options: ChatOptions = {}
): Promise<string> {
    const { mcpState, onMcpToolCall } = options;
    const cliModeEnabled = options.cliMode?.enabled ?? false;

    // Default timeout: 5 minutes for non-interactive mode
    const STREAM_TIMEOUT_MS = 300_000;

    return new Promise((resolve, reject) => {
        let responseText = '';
        let settled = false;

        const settle = () => {
            if (settled) return false;
            settled = true;
            clearTimeout(timeoutId);
            return true;
        };

        // Timeout guard: reject if stream hangs (network drop, server crash, etc.)
        const timeoutId = setTimeout(() => {
            if (settle()) {
                agentService.abort();
                reject(new Error(
                    'Request timed out after 5 minutes. The server may be unresponsive.\n' +
                    'Check your network connection and try again.'
                ));
            }
        }, STREAM_TIMEOUT_MS);

        const callbacks: AgentStreamCallbacks = {
            onContent: (text, partial) => {
                if (!partial) {
                    responseText = text;
                } else {
                    responseText += text;
                }
            },

            onMcpToolCall: async (callId, serverName, toolName, args) => {
                if (onMcpToolCall) {
                    return onMcpToolCall(callId, serverName, toolName, args);
                }
                return {
                    content: [{ type: 'text' as const, text: 'MCP not available' }],
                    isError: true,
                };
            },

            onDone: () => {
                if (settle()) {
                    // Render based on mode
                    const rendered = cliModeEnabled
                        ? responseText
                        : renderMarkdown(responseText);
                    resolve(rendered.trim());
                }
            },

            onError: (error) => {
                if (settle()) {
                    reject(new Error(error));
                }
            },
        };

        agentService.streamMessage(message, callbacks, {
            mcpState,
            cliMode: cliModeEnabled ? { enabled: true, cwd: process.cwd(), platform: process.platform, hostname: os.hostname(), channel: 'cli' } : undefined,
        }).catch((error) => {
            if (settle()) {
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    });
}
